import React, { useState, useEffect } from 'react';
import { FaUpload } from "react-icons/fa";
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError } from "../../utils/alertHelper";

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

export default function ItemCreate() {
  const { id: item_id } = useParams();
  const navigate = useNavigate();

  const [itemCode, setItemCode] = useState('');
  const [itemName, setItemName] = useState('');
  const [itemAvailable, setItemAvailable] = useState(false);
  const [showInPdf, setShowInPdf] = useState(false);
  const [showOnProposal, setShowOnProposal] = useState(false);
  const [image, setImage] = useState(null); // File object (new) or { url } (existing)
  const [previewUrl, setPreviewUrl] = useState('');
  const [variants, setVariants] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);

  useEffect(() => {
    async function fetchData() {
      try {
        const businessId = localStorage.getItem('businessId');
        if (!businessId) {
          toastError("Business ID not found.");
          return;
        }

        // Fetch units
        const unitsRes = await axios.get(`/admin/api/item-unit?business=${businessId}`);
        if (unitsRes.data && unitsRes.data.units) {
          setUnitOptions(unitsRes.data.units);
        }

        // If editing, fetch item
        if (item_id) {
          const itemRes = await axios.get(`/api/item/${item_id}`);
          if (itemRes.data && itemRes.data.item) {
            mapFetchedItemToState(itemRes.data.item);
          }
        } else {
          // Default two empty variants when creating
          setVariants([getEmptyVariant(), getEmptyVariant()]);
        }
      } catch (err) {
        console.error(err);
        toastError("Failed to fetch data. Please try again.");
      }
    }
    fetchData();
  }, [item_id]);

  // Initialize Bootstrap tooltips
  useEffect(() => {
    if (window.bootstrap?.Tooltip) {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipTriggerList.forEach(el => new window.bootstrap.Tooltip(el));
    }
  }, []);

  const getEmptyVariant = () => ({
    _id: undefined, // Only present for existing variants
    variantAvailable: false,
    showInPdf: false,
    companyName: '',
    variant: '',
    rate: '',
    gst: '',
    hsn: '',
    description: '',
    unit: '',
  });

  function mapFetchedItemToState(item) {
    setItemCode(item.code || '');
    setItemName(item.name || '');
    setItemAvailable(item.isAvailable || false);
    setShowInPdf(item.isshow || false);
    setShowOnProposal(item.isshowImage || false);

    // Handle image preview
    if (item.image) {
      const fullUrl = `${IMAGE_BASE_URL}${item.image}`;
      setImage({ url: fullUrl });
      setPreviewUrl(fullUrl);
    } else {
      setImage(null);
      setPreviewUrl('');
    }

    // Map variants, preserving _id for existing ones
    const mappedVariants = item.variants?.length > 0
      ? item.variants.map(v => ({
          _id: v._id,
          variantAvailable: v.isAvailable || false,
          showInPdf: v.isshow || false,
          companyName: v.companyName || '',
          variant: v.name || '',
          rate: v.rate?.toString() || '',
          gst: v.gst?.toString() || '',
          hsn: v.hsn || '',
          description: v.description || '',
          unit: v.unit?._id || '',
        }))
      : [getEmptyVariant(), getEmptyVariant()];

    setVariants(mappedVariants);
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toastError('Only JPG, JPEG, PNG, and PDF files are allowed.');
      e.target.value = '';
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toastError('File size exceeds 2MB.');
      e.target.value = '';
      return;
    }

    setImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleVariantChange = (idx, field, value) => {
    setVariants(prev => {
      const updated = [...prev];
      updated[idx][field] = value;
      return updated;
    });
  };

  const handleAddVariant = () => {
    setVariants(prev => [...prev, getEmptyVariant()]);
  };

  const handleRemoveVariant = (idx) => {
    if (variants.length <= 1) {
      toastError("At least one variant is required.");
      return;
    }
    setVariants(prev => prev.filter((_, i) => i !== idx));
  };

  const handleClear = () => {
    setItemCode('');
    setItemName('');
    setItemAvailable(false);
    setShowInPdf(false);
    setShowOnProposal(false);
    setImage(null);
    setPreviewUrl('');
    setVariants([getEmptyVariant(), getEmptyVariant()]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!itemName.trim()) {
      toastError("Item Name is required.");
      return;
    }

    try {
      const businessId = localStorage.getItem('businessId');
      const formData = new FormData();

      formData.append('code', itemCode);
      formData.append('name', itemName);
      formData.append('isAvailable', itemAvailable);
      formData.append('isshow', showInPdf);
      formData.append('isshowImage', showOnProposal);
      formData.append('business', businessId);

      // Append new image only if a new file is selected
      if (image && image instanceof File) {
        formData.append('image', image);
      }

      // Prepare variants – include _id only for existing variants
      const cleanedVariants = variants.map(v => ({
        ...(v._id && { _id: v._id }),
        name: v.variant,
        companyName: v.companyName,
        unit: v.unit,
        hsn: v.hsn,
        rate: v.rate,
        gst: v.gst,
        description: v.description,
        isAvailable: v.variantAvailable,
        isshow: v.showInPdf
      }));

      formData.append('variants', JSON.stringify(cleanedVariants));

      if (item_id) {
        await axios.put(`/api/item/${item_id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toastSuccess('Item updated successfully!');
      } else {
        await axios.post('/api/item', formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toastSuccess('Item created successfully!');
        handleClear();
      }

      navigate('/user/item');
    } catch (err) {
      console.error(err);
      toastError(err.response?.data?.message || 'Failed to submit item.');
    }
  };

  return (
    <div className="container-fluid py-4 px-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h2 fw-bold">{item_id ? "Edit Item" : "Add New Item"}</h1>
        <a href="/user/item" className="text-decoration-none text-secondary">
          ← Back to Items
        </a>
      </div>

      <form
        className="rounded-3 p-4"
        style={{ background: "#F6F6F6", border: "1.5px solid #BBBFD2" }}
        onSubmit={handleSubmit}
      >
        {/* Item Code & Name */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <label className="form-label text-muted">Item Code</label>
            <input
              type="text"
              className="form-control rounded-3"
              value={itemCode}
              onChange={e => setItemCode(e.target.value)}
            />
          </div>
          <div className="col-md-5">
            <label className="form-label text-muted">
              Item Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control rounded-3"
              value={itemName}
              onChange={e => setItemName(e.target.value)}
              required
            />
          </div>
        </div>
        <hr className="my-4" />
        {/* Switches */}
        <div className="row mb-4">
          <div className="col-md-4">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={itemAvailable}
                onChange={e => setItemAvailable(e.target.checked)}
                id="itemAvailable"
              />
              <label className="form-check-label" htmlFor="itemAvailable">
                Item Available?
              </label>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={showInPdf}
                onChange={e => setShowInPdf(e.target.checked)}
                id="showInPdf"
              />
              <label className="form-check-label" htmlFor="showInPdf">
                Show in PDF
              </label>
            </div>
          </div>
        </div>

        <hr className="my-4" />
        {/* Variants Section */}
        <h5 className="mb-4">Variants</h5>

        {variants.map((variant, idx) => (
          <div key={idx} className="border rounded-3 p-4 mb-4 bg-light">
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={variant.variantAvailable}
                    onChange={e => handleVariantChange(idx, 'variantAvailable', e.target.checked)}
                    id={`variantAvailable-${idx}`}
                  />
                  <label className="form-check-label" htmlFor={`variantAvailable-${idx}`}>
                    Variant Available?
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={variant.showInPdf}
                    onChange={e => handleVariantChange(idx, 'showInPdf', e.target.checked)}
                    id={`showInPdf-${idx}`}
                  />
                  <label className="form-check-label" htmlFor={`showInPdf-${idx}`}>
                    Show in Quotation
                  </label>
                </div>
              </div>
            </div>

            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label">Company Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={variant.companyName}
                  onChange={e => handleVariantChange(idx, 'companyName', e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">
                  Variant <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={variant.variant}
                  onChange={e => handleVariantChange(idx, 'variant', e.target.value)}
                  required
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">
                  Rate (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={variant.rate}
                  onChange={e => handleVariantChange(idx, 'rate', e.target.value)}
                  required
                />
              </div>
              <div className="col-md-1">
                <label className="form-label">GST (%)</label>
                <input
                  type="number"
                  className="form-control"
                  value={variant.gst}
                  onChange={e => handleVariantChange(idx, 'gst', e.target.value)}
                />
              </div>
              <div className="col-md-1">
                <label className="form-label">HSN</label>
                <input
                  type="text"
                  className="form-control"
                  value={variant.hsn}
                  onChange={e => handleVariantChange(idx, 'hsn', e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-control"
                  value={variant.description}
                  onChange={e => handleVariantChange(idx, 'description', e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">
                  Unit <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={variant.unit}
                  onChange={e => handleVariantChange(idx, 'unit', e.target.value)}
                  required
                >
                  <option value="">Select Unit</option>
                  {unitOptions.map(unit => (
                    <option key={unit._id} value={unit._id}>
                      {unit.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-1">
                <button
                  type="button"
                  className="btn btn-outline-danger w-100"
                  onClick={() => handleRemoveVariant(idx)}
                >
                  -
                </button>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-outline-primary mb-4"
          onClick={handleAddVariant}
        >
          + Add
        </button>

        <div className="d-flex justify-content-end gap-3">
          <button type="button" className="btn btn-secondary" onClick={handleClear}>
            Clear
          </button>
          <button type="submit" className="btn btn-primary btn-lg">
            {item_id ? 'Update' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}